<script lang="ts">
  import type { AddressValue } from "$src/domain/core/AddressValue";
  import { createEventDispatcher, getContext } from "svelte";
  import { ContextTokens } from "../contexts/ContextTokens";
  import type { AuthContext } from "$src/infrastructure/accounts/AuthContext";
  import { Label } from "$lib/components/ui/label";
  import { Input } from "$lib/components/ui/input";
  import { t } from "svelte-i18n";
  import CountryInput from "./localities/components/country-input.svelte";
  import LocalityInput from "./localities/components/locality-input.svelte";
  import PostalCodeInput from "./localities/components/postal-code-input.svelte";
  import { type Locality } from "$src/domain/map/Locality";
  import { languageCode } from "$src/presentation/core/languageCode";
  import { debounce } from "$src/application/main/debounce";
  import { useComputeGeocode, computeGeocode } from "./hooks/useGeocode";

  export let value: AddressValue;
  export let editMode: boolean = false;
  export let googleLink: boolean = false;
  export let geoCodeActivated: boolean = false;
  export let displayGeoCode: boolean = false;

  let progress = 0;
  let interval: NodeJS.Timeout;

  let { activeGroupStore }: AuthContext = getContext(ContextTokens.Auth);

  const dispatch = createEventDispatcher();
  const geoCodeCommand = useComputeGeocode();

  export let patchHandler: () => void = () => {};

  function selectPostalCode(e: CustomEvent<any>): void {
    var locality: Locality = e.detail;

    value.postalCode = locality.postalCode;
    value.locality = locality.name;

    startGeoCode();
  }

  function selectLocality(e: CustomEvent<any>): void {
    var locality: Locality = e.detail;

    value.postalCode = locality.postalCode;
    value.locality = locality.name;

    startGeoCode();
  }

  function startProgressAnimation() {
    progress = 100; // Initialiser la progression à 100%
    clearInterval(interval); // Assurez-vous d'effacer l'intervalle précédent
    interval = setInterval(() => {
      if (progress > 0) {
        progress -= 1; // Diminue la progression
      }
    }, 40); // Réduire la barre progressivement
  }

  function startGeoCode() {
    debouncedGeocode();
    startProgressAnimation();
  }

  const debouncedGeocode = debounce(() => {
    geoCodeAddress();
  }, 4000);

  async function geoCodeAddress() {
    clearInterval(interval); // Arrête l'animation lorsque l'appel est terminé
    progress = 0; // Réinitialiser la barre à 0 lorsque l'appel est terminé

    if (
      geoCodeActivated &&
      value.locality &&
      value.locality.length > 1 &&
      value.address &&
      value.address.length > 1 &&
      value.postalCode &&
      value.postalCode.length > 1 &&
      value.countryCode &&
      value.countryCode.length == 2
    ) {
      var geoCode = await $geoCodeCommand.mutateAsync({ address: value });
      dispatch("geoCoded", geoCode);
    }
  }
</script>

{#if !editMode}
  <p>
    {#if googleLink}
      <a
        target="_blank"
        class="hover:text-secondary-foreground"
        href={`https://www.google.com/maps/place/${value.address},+${value.postalCode},+${value.locality},+${value.countryCode}`}
      >
        {value.address} <br />
        {value.postalCode}
        {value.locality} - {value.countryCode}
      </a>
    {:else}
      {value.address} <br />
      {value.postalCode}
      {value.locality} - {value.countryCode}
    {/if}
  </p>
{:else}
  <div class="grid w-full grid-cols-2 gap-2 max-w-md">
    <div class="col-span-full">
      <Label for="countryCode" class="text-xs text-muted-foreground">
        {$t("global.fields.countryCode")}
      </Label>
      <CountryInput
        bind:value={value.countryCode}
        languageCode={$languageCode}
        {patchHandler}
        inputHandler={startGeoCode}
      />
    </div>
    <div class="col-span-full">
      <Label for="address" class="text-xs text-muted-foreground">
        {$t("global.fields.address")}
      </Label>
      <Input
        type="text"
        maxlength={250}
        id="address"
        bind:value={value.address}
        on:input={patchHandler}
        on:input={startGeoCode}
      />
    </div>
    <div class="col-span-full md:col-span-1">
      <Label for="postalCode" class="text-xs text-muted-foreground">
        {$t("global.fields.postalCode")}
      </Label>
      <PostalCodeInput
        bind:value={value.postalCode}
        countryCode={value.countryCode ?? "FR"}
        on:selectLocality={selectPostalCode}
        {patchHandler}
        inputHandler={startGeoCode}
      />
    </div>
    <div class="col-span-full md:col-span-1">
      <Label for="locality" class="text-xs text-muted-foreground">
        {$t("global.fields.locality")}
      </Label>
      <LocalityInput
        bind:value={value.locality}
        countryCode={value.countryCode ?? "FR"}
        on:selectLocality={selectLocality}
        {patchHandler}
        inputHandler={startGeoCode}
      />
    </div>
  </div>

  {#if displayGeoCode}
    <div class="progress-bar bg-primary/30" style="width: {progress}%;"></div>
  {/if}
{/if}

<style>
  .progress-bar {
    height: 4px;
    transition: width 0.5s linear;
  }
</style>
